/**
 * The shop-URL category picker for WooCommerce and Square Online — the store
 * counterpart of the Shopify product-type step, with the same rules:
 *
 *  - one row per category, with its product count, sorted most products first
 *    (Shopify's getProductTypes);
 *  - default ticks: the source's saved exclusions when it has any (an empty
 *    list means "reviewed, nothing excluded"), else the isLikelyWatch heuristic;
 *  - products whose title carries an accessory word and nothing that marks a
 *    watch are excluded, with a per-product "include anyway" override; a
 *    lifecycle word (coming soon, discontinued…) always excludes.
 *
 * Store-specific: a product filed in several categories (common on
 * WooCommerce) goes on when ANY of its categories is ticked, and a category
 * none of whose products survive the title/lifecycle rules is not offered.
 */
import type { ScrapedWatchEntry } from './BulkImport';
import { hasLifecycleKeyword, isLikelyWatch, titleExclusionKeyword } from './ShopifyScraper';

/** The bucket for products filed in no category (Shopify's "(none)" type). */
export const NO_CATEGORY = '__none__';

type StoreEntry = Pick<
  ScrapedWatchEntry,
  'reference' | 'productName' | 'model' | 'title' | 'storeCategories'
> & { wooProductId?: string; squareProductId?: string };

export interface StoreCategoryOption {
  /** The shop's category id, or {@link NO_CATEGORY}. */
  key: string;
  /** "Parent › Child" when the shop's hierarchy is known, else the name. */
  label: string;
  /** Products (not variant rows) in this category that survive the title/lifecycle rules. */
  count: number;
  likelyWatch: boolean;
}

/** One product per store product id — variant rows of a product share it. */
export function storeProductKey(e: StoreEntry): string {
  return e.wooProductId ?? e.squareProductId ?? e.reference;
}

/** The title the product-level rules read: the product's own name. */
function productTitle(e: StoreEntry): string {
  return e.productName || e.model || e.title || '';
}

function categoryKeys(e: StoreEntry): string[] {
  const keys = (e.storeCategories ?? []).map((c) => c.id);
  return keys.length > 0 ? keys : [NO_CATEGORY];
}

/** Title rule for one product, lifting an accessory match the curator overrode. */
function survivesTitleRules(e: StoreEntry, overrides?: ReadonlySet<string>): boolean {
  const title = productTitle(e);
  if (hasLifecycleKeyword(title)) return false;
  if (titleExclusionKeyword(title) && !overrides?.has(storeProductKey(e))) return false;
  return true;
}

/**
 * The picker's rows. `parents` maps a category id to its parent's (WooCommerce
 * `parent`; absent for Square, whose storefront list is flat) and `names` to a
 * name, for "Parent › Child" labels — the list itself stays flat, as Shopify's.
 */
export function storeCategoryOptions(
  entries: readonly StoreEntry[],
  hierarchy?: { names: ReadonlyMap<string, string>; parents: ReadonlyMap<string, string> },
  overrides?: ReadonlySet<string>,
): StoreCategoryOption[] {
  const products = new Map<string, Set<string>>();
  const names = new Map<string, string>();
  for (const e of entries) {
    if (!survivesTitleRules(e, overrides)) continue;
    for (const c of e.storeCategories ?? []) names.set(c.id, c.name);
    for (const key of categoryKeys(e)) {
      if (!products.has(key)) products.set(key, new Set());
      products.get(key)!.add(storeProductKey(e));
    }
  }
  const labelOf = (key: string): string => {
    if (key === NO_CATEGORY) return '(none) — no category';
    const own = names.get(key) ?? hierarchy?.names.get(key) ?? `Category ${key}`;
    const parentId = hierarchy?.parents.get(key);
    const parent = parentId && parentId !== '0' ? hierarchy?.names.get(parentId) : undefined;
    return parent ? `${parent} › ${own}` : own;
  };
  return [...products.entries()]
    .map(([key, set]) => {
      const label = labelOf(key);
      return { key, label, count: set.size, likelyWatch: key !== NO_CATEGORY && isLikelyWatch(names.get(key) ?? label) };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * Initial ticks. Saved exclusions win when the source has any — even an empty
 * list, which means "reviewed, nothing excluded"; otherwise the heuristic.
 */
export function defaultStoreCategorySelection(
  options: readonly StoreCategoryOption[],
  savedExcluded: readonly string[] | undefined,
): Set<string> {
  if (savedExcluded !== undefined) {
    const excluded = new Set(savedExcluded);
    return new Set(options.filter((o) => !excluded.has(o.key)).map((o) => o.key));
  }
  return new Set(options.filter((o) => o.likelyWatch).map((o) => o.key));
}

/** The rows that go on to the preview: in ANY ticked category, and past the title rules. */
export function filterStoreEntries<E extends StoreEntry>(
  entries: readonly E[],
  selected: ReadonlySet<string>,
  overrides?: ReadonlySet<string>,
): E[] {
  return entries.filter(
    (e) => categoryKeys(e).some((k) => selected.has(k)) && survivesTitleRules(e, overrides),
  );
}

/**
 * Products in a ticked category that the TITLE rule alone keeps out, one per
 * product, with the word that did it — the "excluded by title" list. A product
 * with a lifecycle word stays out even if ticked, so it is not offered.
 */
export function titleExcludedStoreProducts(
  entries: readonly StoreEntry[],
  selected: ReadonlySet<string>,
): Array<{ key: string; title: string; keyword: string }> {
  const out = new Map<string, { key: string; title: string; keyword: string }>();
  for (const e of entries) {
    const key = storeProductKey(e);
    if (out.has(key) || !categoryKeys(e).some((k) => selected.has(k))) continue;
    const title = productTitle(e);
    if (hasLifecycleKeyword(title)) continue;
    const keyword = titleExclusionKeyword(title);
    if (keyword) out.set(key, { key, title, keyword });
  }
  return [...out.values()];
}
